import React, { useEffect, useState } from 'react';
import { Grid, GridColumn as Column, GridNoRecords } from '@progress/kendo-react-grid';
import UserDisplay from '../Shared/User/UserDisplay';
import { useTenantsApp } from '../../Context/TenantsApp/TenantsAppContext';
import { DELETE_TENANT, DESELECT_ALL_TENANT_IDS, DESELECT_TENANT_ID, ENABLE_TENANT, SELECT_ALL_TENANT_IDS, SELECT_TENANT_ID, SET_TENANT_ID, TOGGLE_DELETE_APP_DIALOG, TOGGLE_DISABLE_APP_DIALOG, TOGGLE_SYNC_TENANT_DIALOG, UPDATE_TENANT_BY_TENANT_ID } from './TenantsAppEvents';
import { io } from 'socket.io-client';
import { Checkbox } from '@progress/kendo-react-inputs';
import { Menu, MenuItem } from "@progress/kendo-react-layout";
import { useTranslation } from 'react-i18next';
import { usePropsStoreValue } from '../../Hooks/PropStore/usePropsStore';
import InnerLoader from '../Shared/Loader/InnerLoader';
import { enableAllInstancesOfApplicationOnRuntime } from '../Helpers/Runtime/runtime';
import { getProject, updateProject } from '../../API/Project/project';
import { useMsal } from '@azure/msal-react';
import { OverlayLoader } from '../Shared/Loader/OverlayLoader';
import { updateTenantProject } from '../../API/Tenant/Tenant';

const ActionCell = ({ dataItem }) => {

    const context = useMsal();
    const { state, dispatch } = useTenantsApp();
    const [inProgress, setInProgress] = useState(false);

    const { t } = useTranslation([
        "tenant/tenant",
    ], {
        useSuspense: false
    })

    const shouldSyncDisabled = !dataItem.tenantOrganizationId || !dataItem.masterOrganizationId || !dataItem.project.tenantProjectId || !dataItem.project.masterProjectId

    const handleDeleteApp = () => {
        dispatch({ type: SET_TENANT_ID, payload: dataItem._id });
        dispatch({ type: DESELECT_ALL_TENANT_IDS });
        dispatch({ type: TOGGLE_DELETE_APP_DIALOG });
    }

    const handleDisableApp = () => {
        dispatch({ type: SET_TENANT_ID, payload: dataItem._id });
        dispatch({ type: DESELECT_ALL_TENANT_IDS });
        dispatch({ type: TOGGLE_DISABLE_APP_DIALOG });
    }

    const handleSyncApp = () => {
        dispatch({ type: SET_TENANT_ID, payload: dataItem._id });
        dispatch({ type: TOGGLE_SYNC_TENANT_DIALOG });
    }

    const handleEnableApp = async () => {
        setInProgress(true);
        const projectToDelete = await getProject(dataItem.project.tenantProjectId)
        if (!projectToDelete) {
            dispatch({ type: DELETE_TENANT, payload: state.selectedTenantId });
            setInProgress(false);
            return;
        }

        const [disableStatus, disableMessage] = await enableAllInstancesOfApplicationOnRuntime(
            context, projectToDelete.organizationId, projectToDelete.instances, projectToDelete.name
        );

        if (disableStatus === "error") {
            setInProgress(false);
            return;
        }

        await Promise.allSettled([
            updateProject(projectToDelete._id, {
                isActive: true,
                deletedBy: null,
                deletedOn: null
            }),
            updateTenantProject(dataItem._id, dataItem.project.masterProjectId, { status: "accepted" })
        ])

        dispatch({ type: ENABLE_TENANT, payload: { tenantId: dataItem._id } });
        setInProgress(false);
    }

    const isCurrentlySyncing = dataItem.syncStatus === "In Progress" || dataItem.syncStatus === "Queued";
    const isAppValid = state.project.isActive && !state.project.hasSchemaChanges

    return (
        <td className='text-end'>
            {inProgress && <OverlayLoader />}
            {inProgress && <InnerLoader borderColor="green" />}
            <button
                onClick={handleSyncApp}
                disabled={shouldSyncDisabled || isCurrentlySyncing || !isAppValid}
                className={`btn btn-sm ${isCurrentlySyncing ? 'btn-success' : 'btn-primary'}`}
            >
                {isCurrentlySyncing ?
                    <i class="fas fa-sync fa-spin me-2"></i>
                    :
                    <i class="fas fa-sync me-2"></i>
                }
                {isCurrentlySyncing ?
                    t(dataItem.syncStatus.replace(" ", "_").toLowerCase())
                    :
                    t("sync_button")
                }
            </button>
            <Menu
                onSelect={(e) => e.item.data()} className="no-dropdown-in-menu three-dots d-inline-block">
                <MenuItem
                    disabled={isCurrentlySyncing || shouldSyncDisabled}
                    cssClass="btn btn-secondary px-2"
                    data={() => { }}
                    linkRender={props => (
                        <span aria-label="extra-options" className="k-icon k-i-more-horizontal g-0 m-0 px-3"></span>
                    )}
                >
                    <MenuItem
                        cssClass="bg-white hover-border-0"
                        text={t("delete_button")}
                        data={handleDeleteApp}
                    />
                    <MenuItem
                        cssClass="bg-white hover-border-0"
                        text={dataItem.project.tenantProject.isActive ? t("disable_button") : t("enable_button")}
                        data={dataItem.project.tenantProject.isActive ? handleDisableApp : handleEnableApp}
                    />
                </MenuItem>
            </Menu>
        </td>
    );
};

const SelectAllCheckbox = () => {

    const { state, dispatch } = useTenantsApp();

    const isAnyTenantSyncing = state.tenants.some(tenant => tenant.syncStatus === "In Progress" || tenant.syncStatus === "Queued");

    const isAllSelected = state.tenants.length > 0 && state.selectedTenantIds.length === state.tenants.length;
    const isAppValid = state.project.isActive && !state.project.hasSchemaChanges

    const handleSelectAll = (event) => {
        if (event.target.value) {
            dispatch({ type: SELECT_ALL_TENANT_IDS });
        }
        else {
            dispatch({ type: DESELECT_ALL_TENANT_IDS });
        }
    }

    if (state.tenants.length === 0) {
        return null;
    }

    return (
        <Checkbox
            disabled={isAnyTenantSyncing || !isAppValid}
            className="k-checkbox-no-spacing"
            checked={isAllSelected}
            onChange={handleSelectAll}
        />
    );
};

const TenantsAppGrid = () => {
    const { state, dispatch } = useTenantsApp();

    const project = usePropsStoreValue("project");

    const { t } = useTranslation([
        "tenant/tenant",
    ], {
        useSuspense: false
    })

    const isAnyTenantSyncing = state.tenants.some(tenant => tenant.syncStatus === "In Progress" || tenant.syncStatus === "Queued");
    const isAppValid = state.project.isActive && !state.project.hasSchemaChanges
    // Listen for Socket.io events from BullMQ
    useEffect(() => {
        const url = process.env.REACT_APP_DESIGNER_API_URL
        const socket = io(url);
        socket.on('updateTenants', (update) => {
            if (project._id !== update.masterProjectId) return;
            for (const tenant of update.tenants) {
                dispatch({
                    type: UPDATE_TENANT_BY_TENANT_ID,
                    payload: {
                        tenantId: tenant.tenantId,
                        data: {
                            syncStatus: tenant.syncStatus,
                            lastSyncedAt: tenant.lastSyncedAt,
                            error: tenant.error
                        }
                    }
                });
            }
        });

        socket.on('deleteTenant', (update) => {
            if (!state.showDeleteAppDialog) {
                dispatch({ type: SET_TENANT_ID, payload: null });
                dispatch({ type: TOGGLE_DELETE_APP_DIALOG });
                dispatch({
                    type: DELETE_TENANT,
                    payload: update.tenantId
                })
            }
        });

        // Cleanup event listeners on unmount
        return () => {
            socket.off('tenantUpdate');
            socket.off('deleteTenant');
        };
        // eslint-disable-next-line
    }, []);

    const handleSelect = (event, id) => {
        if (event.target.value) {
            dispatch({ type: SELECT_TENANT_ID, payload: id });
        }
        else {
            dispatch({ type: DESELECT_TENANT_ID, payload: id });
        }
    }

    const handleSelectAll = (event) => {

    }

    return (
        <Grid data={state.tenants}>
            <GridNoRecords>
                <small className="text-muted text-center">
                    {t("no_tenants_found")}
                </small>
            </GridNoRecords>
            <Column width={40}
                cell={(props) => (
                    <td>
                        <Checkbox
                            disabled={props.dataItem.syncStatus === "In Progress" || props.dataItem.syncStatus === "Queued" || isAnyTenantSyncing || !isAppValid}
                            checked={state.selectedTenantIds.includes(props.dataItem._id)}
                            onChange={(event) => handleSelect(event, props.dataItem._id)}
                        />
                    </td>
                )}
                headerCell={() => (
                    <SelectAllCheckbox
                        data={state.tenants}
                        onClick={handleSelectAll}
                    />
                )}
            />
            <Column
                width={40}
                title="#"
                cell={props => <td>{props.dataIndex + 1}</td>}
            />
            <Column
                field='tenantName'
                title={t("tenant_name")}
            />
            <Column
                width={250}
                title={t("owner")}
                field="tenantEmail"
                cell={({ dataItem }) =>
                    <td>
                        <UserDisplay
                            bgColor={dataItem.tenantUser.bgColor || "#000"}
                            email={dataItem.tenantUser.email || dataItem.tenantEmail}
                            firstName={dataItem.tenantUser.firstName || ""}
                            lastName={dataItem.tenantUser.lastName || ""}
                            displayName={dataItem.tenantUser.displayName || ""}
                            showEmail={dataItem.tenantUser.email ? true : false}
                        />
                    </td>
                }
            />
            <Column
                title={t("status")}
                field="status"
                cell={({ dataItem }) => {
                    if (dataItem.project.status !== "disabled") {
                        return (
                            <td>
                                <span className="badge bg-success text-white">
                                    {t("active")}
                                </span>
                            </td>
                        );
                    } else {
                        return (
                            <td>
                                <span className="badge bg-danger text-white">
                                    {t("not_active")}
                                </span>
                            </td>
                        );
                    }
                }}
            />
            <Column
                title={t("last_synced_on")}
                field="lastSyncedAt"
                cell={({ dataItem }) => {
                    if (dataItem.lastSyncedAt && dataItem.lastSyncedAt !== "Invalid Date") {
                        return (
                            <td>{new Date(dataItem.lastSyncedAt).toLocaleString()}</td>
                        );
                    }
                    return (
                        <td>
                            <span className="text-muted">
                                App not synced yet
                            </span>
                        </td>
                    );
                }}
            />
            <Column
                title={t("last_sync_status")}
                field="syncStatus"
                cell={({ dataItem }) => {
                    if (dataItem.syncStatus === "In Progress") {
                        return (
                            <td>
                                <span className="badge bg-warning text-dark">
                                    {t(dataItem.syncStatus.replace(" ", "_").toLowerCase())}
                                </span>
                            </td>
                        );
                    } else if (dataItem.syncStatus === "Queued") {
                        return (
                            <td>
                                <span className="badge bg-info text-dark">
                                    {t(dataItem.syncStatus.replace(" ", "_").toLowerCase())}
                                </span>
                            </td>
                        );
                    } else if (dataItem.syncStatus === "Completed") {
                        return (
                            <td>
                                <span className="badge bg-success text-white">
                                    {t(dataItem.syncStatus.replace(" ", "_").toLowerCase())}
                                </span>
                            </td>
                        );
                    } else {
                        return (
                            <td>
                                <span className="badge bg-danger text-white">
                                    {t(dataItem.syncStatus.replace(" ", "_").toLowerCase())}
                                </span>
                            </td>
                        );
                    }
                }}
            />
            <Column
                title={t("last_synced_error")}
                field="error"
                cell={({ dataItem }) => {
                    if (dataItem.error) {
                        return (
                            <td>
                                <div style={{
                                    whiteSpace: 'pre-wrap',
                                    maxHeight: '100px',
                                    overflowY: 'auto'
                                }}>
                                    {dataItem.error}
                                </div>
                            </td>
                        );
                    }
                    return <td></td>;
                }}
            />
            <Column
                width={250}
                cell={ActionCell}
            />
        </Grid>
    )
}

export default TenantsAppGrid;